import { Button, Menu, Slide } from '@material-ui/core'
import React from 'react'
import { useSelector } from 'react-redux'
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

export default function ChartHours({
    afiliado = {},
    items = [],
    chartData = [],
    bestHour = {},
    bestWindow = {},
    keyPrefix = 'campaign_',
    titleField = 'title',
    title = 'Horários que mais vendem',
    periodKey = 'period_hour',
    defaultPeriod = '24h',
    presets = ['24h', '7d', '1m', '3m', 'all'],
    legend = true,
    onChange,
}) {
    const { theme, colorsLight, colorsDark } = useSelector(state => state.appReducer)

    const [period, setPeriod] = React.useState(defaultPeriod)
    const [colors, setColors] = React.useState(localStorage.getItem('data-bs-theme') === 'dark' ? colorsDark : colorsLight)

    const [anchorEl, setAnchorEl] = React.useState(null)
    const [othersData, setOthersData] = React.useState([])

    const openOthers = Boolean(anchorEl)

    React.useEffect(() => {
        if(theme === 'dark') {
            setColors(colorsDark)
        }else{
            setColors(colorsLight)
        }
    },[theme])

    React.useEffect(() => {
        onChange?.({
            [periodKey]: period
        })
    }, [period, periodKey, onChange])

    const formatNumber = (value) => {
        if (value >= 1000000) {
            const num = value / 1000000
            return (Number.isInteger(num) ? num : num.toFixed(1).replace('.', ',')) + 'M'
        }

        if (value >= 1000) {
            const num = value / 1000
            return (Number.isInteger(num) ? num : num.toFixed(1).replace('.', ',')) + 'k'
        }

        return value
    }

    const formatMoney = (value) => {
        return Number(value || 0).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        })
    }

    const formatLiquid = (value) => {
        let liquid = (value * afiliado?.afiliado_percent) / 100;
        
        return Number(liquid || 0).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }) 
    }

    const dataBar = React.useMemo(() => {
        if (!items?.length || !chartData?.length) return []

        return items
            .map(item => {
                const key = `${keyPrefix}${item.id}`

                const total = chartData.reduce((sum, row) => {
                    return sum + Number(row[key] || 0)
                }, 0)

                return {
                    id: item.id,
                    key,
                    title: item[titleField],
                    total
                }
            })
            .sort((a, b) => b.total - a.total)
    }, [items, chartData, keyPrefix, titleField])

    const legendData = React.useMemo(() => {
        return dataBar.map((item, i) => ({
            ...item,
            color: colors[i % colors.length]
        }))
    }, [dataBar, colors])

    const legendDisplayData = React.useMemo(() => {
        const maxItems = 6;

        if (!legendData?.length) return [];

        if (legendData.length <= maxItems) {
            return legendData;
        }

        const topItems = legendData.slice(0, maxItems - 1);
        const others = legendData.slice(maxItems - 1);

        const othersTotal = others.reduce((sum, item) => sum + Number(item.total || 0), 0);

        return [
            ...topItems,
            {
                key: 'others',
                title: `+ ${others.length} outros`,
                total: othersTotal,
                color: '#64748b',
                isOthers: true,
                items: others
            }
        ];
    }, [legendData])

    const metaMap = React.useMemo(() => {
        return legendData.reduce((acc, item) => {
            acc[item.key] = item
            return acc
        }, {})
    }, [legendData])

    const highlightedChartData = React.useMemo(() => {
        const windowBlocks = bestWindow?.is_strong ? (bestWindow?.data?.blocks || []) : []
        const bestBlock = bestHour?.block

        return chartData.map(row => ({
            ...row,
            is_peak: windowBlocks.length
                ? windowBlocks.includes(row.block)
                : row.block === bestBlock
        }))
    }, [chartData, bestHour, bestWindow])

    const CustomTooltip = ({ active, payload, label }) => {
        if (!active || !payload || !payload.length) return null

        const itemsTooltip = payload
            .map(entry => {
                const meta = metaMap[entry.dataKey]

                return {
                    key: entry.dataKey,
                    name: meta?.title || entry.name,
                    color: meta?.color || entry.color,
                    value: Number(entry.value || 0)
                }
            })
            .filter(item => item.value > 0)
            .sort((a, b) => b.value - a.value)

        if (!itemsTooltip.length) return null

        const total = itemsTooltip.reduce((sum, item) => sum + item.value, 0)

        return (
            <div
                style={{
                    backgroundColor: 'var(--bs-chart-card-bg)',
                    border: '1px solid var(--bs-chart-card-border)',
                    borderRadius: 12,
                    padding: '12px 14px',
                    zIndex: 999999,
                    width: 320,
                    maxWidth: '82vw',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.25)'
                }}
            >
                <div
                    style={{
                        color: 'var(--bs-chart-card-title)',
                        fontSize: 14,
                        marginBottom: 10,
                        fontWeight: 600,
                        lineHeight: 1.35,
                        wordBreak: 'break-word'
                    }}
                >
                    {payload?.[0]?.payload?.label_full || label}
                </div>

                {itemsTooltip.map(item => (
                    <div
                        key={item.key}
                        style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            gap: 10,
                            marginBottom: 10
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                flex: 1,
                                minWidth: 0,
                                marginRight: 8
                            }}
                        >
                            <span
                                style={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: 3,
                                    backgroundColor: item.color,
                                    display: 'inline-block',
                                    marginRight: 8,
                                    marginTop: 4,
                                    flexShrink: 0
                                }}
                            />

                            <span
                                style={{
                                    color: 'var(--bs-chart-card-color)',
                                    fontWeight: 600,
                                    fontSize: 14,
                                    lineHeight: 1.3,
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'anywhere',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden'
                                }}
                            >
                                {item.name}
                            </span>
                        </div>

                        <span
                            style={{
                                color: 'var(--bs-chart-card-color)',
                                fontWeight: 700,
                                fontSize: 14,
                                whiteSpace: 'nowrap',
                                flexShrink: 0
                            }}
                        >
                            {formatMoney(item.value)}
                        </span>
                    </div>
                ))}

                <div
                    style={{
                        height: 1,
                        backgroundColor: 'var(--bs-chart-divider)',
                        margin: '15px 0'
                    }}
                />

                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 10
                    }}
                >
                    <span
                        style={{
                            color: 'var(--bs-chart-card-color)',
                            fontWeight: 700,
                            fontSize: 14
                        }}
                    >
                        {Number(afiliado?.afiliado_percent) > 0 ? 'Valor Líquido:' : 'Total'}
                    </span>

                    <span
                        style={{
                            color: 'var(--bs-chart-card-color)',
                            fontWeight: 800,
                            fontSize: 15,
                            whiteSpace: 'nowrap',
                            flexShrink: 0
                        }}
                    >
                        {Number(afiliado?.afiliado_percent) > 0 ? formatLiquid(total) : formatMoney(total)}
                    </span>
                </div>
            </div>
        )
    }

    const formatPresetLabel = (preset) => {
        const map = {
            '24h': '24H',
            '7d': '7D',
            '1m': '1M',
            '3m': '3M',
            '6m': '6M',
            'all': 'TUDO'
        }

        return map[preset] || preset.toUpperCase()
    }

    const totalPeriod = React.useMemo(() => {
        return chartData.reduce((sum, row) => sum + Number(row.total || 0), 0)
    }, [chartData])

    const formatPercent = (value) => {
        const abs = Math.abs(Number(value || 0))

        return `${abs.toLocaleString('pt-BR', {
            minimumFractionDigits: abs % 1 !== 0 ? 1 : 0,
            maximumFractionDigits: 1
        })}%`
    }

    const Transition = React.forwardRef((props, ref) => {
        return <Slide direction="up" ref={ref} {...props} />
    })

    return (
        <>
            {(Boolean(openOthers)) &&
                <Menu
                    anchorEl={anchorEl}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    TransitionComponent={Transition}
                    open={openOthers} 
                    onClose={() => setAnchorEl(null)}
                    className="menu"
                >
                <div className="card-body p-3 bs-bg">
                    {othersData.map(item => (
                        <div
                            key={item.key}
                            className="d-flex align-items-center mb-2"
                        >
                            <span
                                style={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: 3,
                                    backgroundColor: item.color,
                                    display: 'inline-block',
                                    marginRight: 10,
                                    flexShrink: 0
                                }}
                            />

                            <small
                                className="bs-color-b fw-bold"
                                style={{
                                    lineHeight: 1.3,
                                    wordBreak: 'break-word'
                                }}
                            >
                                {item.title}
                            </small>
                        </div>
                    ))}
                </div>
            </Menu>
            }

            <div className="d-flex justify-content-between mb-3">
                <div className="d-flex flex-column">  
                    <label className="fw-bold label-custom bs-color-b mb-2 small">{title}</label>
                    
                    <div className="d-flex flex-column mb-3">
                        <label className="h3 bs-color-b">
                            {formatMoney(totalPeriod)}
                        </label>

                        {Number(afiliado?.afiliado_percent) > 0 && (
                            <div className="d-flex gap-2">
                                <small className="fw-bold bs-color-b">Valor Líquido: </small>
                                <small
                                    style={{
                                        color: 'var(--bs-chart-green)',
                                        fontWeight: 700
                                    }}
                                >
                                    {formatLiquid(totalPeriod)}
                                </small>
                            </div>
                        )}
                    </div>

                    <small
                        style={{
                            color:
                                bestHour?.peak_level === 'very_strong' ? 'var(--bs-chart-green)' :
                                bestHour?.peak_level === 'strong' ? 'var(--bs-text-success)' :
                                bestHour?.peak_level === 'moderate' ? 'var(--bs-chart-warning)' :
                                '#9aa4af',
                            fontWeight: 700
                        }}
                    >
                        {bestWindow?.is_strong
                            ? `🔥 ${bestWindow.data.hours} concentram ${formatPercent(bestWindow.data.percent)}`
                            : bestHour?.peak_level === 'very_strong'
                                ? `🔥🔥 Pico muito forte às ${bestHour?.name} — ${formatPercent(bestHour?.percent)}`
                            : bestHour?.peak_level === 'strong'
                                ? `🔥 Pico às ${bestHour?.name} — ${formatPercent(bestHour?.percent)}`
                            : bestHour?.peak_level === 'moderate'
                                ? `🚀 ${bestHour?.name} concentra ${formatPercent(bestHour?.percent)}`
                            : 'Sem horário dominante'}
                    </small>
                </div>
            </div>

            <div className="chart-container">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={highlightedChartData}
                        margin={{
                            top: 5,
                            right: 10,
                            left: 0,
                            bottom: 5,
                        }}
                        responsive
                    >
                        <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="var(--bs-chart-cartesian)"
                        />

                        <XAxis
                            dataKey="name"
                            stroke="var(--bs-chart-axis)"
                            interval={0}
                            tick={{ fontSize: 12 }}
                            tickLine={false}
                            axisLine={false}
                        />

                        <YAxis
                            stroke="var(--bs-chart-axis)"
                            tickFormatter={(value) => formatNumber(value)}
                            tickLine={false}
                            axisLine={false}
                        />

                        <Tooltip
                            cursor={{ fill: 'var(--bs-chart-fill)' }}
                            content={<CustomTooltip />}
                        />

                        {dataBar.map((item, i) => (
                            <Bar
                                key={item.key}
                                dataKey={item.key}
                                stackId="a"
                                fill={colors[i % colors.length]}
                                name={item.title}
                            >
                                {highlightedChartData.map((entry, index) => {
                                    const baseColor = colors[i % colors.length]

                                    return (
                                        <Cell
                                            key={`${item.key}-${index}`}
                                            fill={baseColor}
                                            fillOpacity={entry.is_peak ? 1 : 0.45}
                                            //stroke={entry.is_peak ? 'var(--bs-chart-cell-stroke)' : 'none'}
                                            strokeWidth={entry.is_peak ? 1.5 : 0}
                                        />
                                    )
                                })}
                            </Bar>
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            </div>

            <div className="mt-3 mb-3">
                <div className="d-flex justify-content-center gap-2 flex-wrap">
                    {presets.map(preset => (
                        <Button
                            key={preset}
                            size="small"
                            className={"bs-bg-invert bs-color-b " + (period === preset && 'btn-checked')}
                            variant="contained"
                            color="primary"
                            onClick={() => setPeriod(preset)}
                        >
                            {formatPresetLabel(preset)}
                        </Button>
                    ))}
                </div>
            </div>
            
            {(legend) &&
            <div className="mt-3 d-flex flex-wrap">
                <div>
                    {legendDisplayData.map(item => (
                        <div
                            key={item.key}
                            className="d-flex align-items-center mb-2"
                            style={{ cursor: item.isOthers ? 'pointer' : 'default' }}
                            onClick={(e) => {
                                if (item.isOthers) {
                                    setOthersData(item.items)
                                    setAnchorEl(e.currentTarget)
                                }
                            }}
                        >
                            <span
                                style={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: 3,
                                    backgroundColor: item.color,
                                    display: 'inline-block',
                                    marginRight: 10,
                                    flexShrink: 0
                                }}
                            />

                            <small
                                className="bs-color-b fw-bold"
                                style={{
                                    lineHeight: 1.3,
                                    wordBreak: 'break-word'
                                }}
                            >
                                {item.title}
                            </small>
                        </div>
                    ))}
                </div>
            </div>
            }
        </>
    )
}